import { tournamentParticipantAPI } from '../../../../tournaments/services/tournamentApi';
import { teamAPI } from '../../../../teams/services/teamApi';

/**
 * Real (non-mock) implementation of this module's participants provider —
 * fetches a tournament's actual enrolled teams/players from the backend,
 * shaped exactly like mockParticipantsService's getMockParticipants output
 * ({id, name, seed, roster?}), so useScheduleBuilder.js and every wizard
 * step treat it identically regardless of source. Only ScheduleBuilderTab.jsx
 * constructs this (see MOCK_PARTICIPANTS_PROVIDER there for the other
 * implementation of the same shape) — nothing else imports this file.
 *
 * Unlike the mock provider, every call here is a real network request, so
 * both functions are async. useScheduleBuilder.js `await`s every provider
 * call for exactly this reason — awaiting a plain synchronous value (what
 * the mock provider returns) is also always safe in JS, so neither provider
 * needed special-casing there.
 */

// "Playing" rows only — coach/referee/scorer/parent-only registrations don't
// compete and would never make sense as a match slot. Mirrors EnrolledTab.jsx's
// own categorization exactly: a TEAM row counts on `teamId` alone (the `player`
// flag isn't reliably set on team registrations — real data has team rows with
// `player: false` that are still real competing entrants); an INDIVIDUAL row
// additionally needs `player === true`, since that's what separates a real
// entrant from a coach/referee/scorer/parent-only registration.
const isPlayingRow = (row) => row.isActive && (row.teamId != null || row.player === true);

const dedupeById = (rows, getId) => {
  const seen = new Map();
  rows.forEach((row) => {
    const id = getId(row);
    if (id != null && !seen.has(id)) seen.set(id, row);
  });
  return Array.from(seen.values());
};

// One large page rather than re-implementing pagination here — real
// tournaments' entrant counts are nowhere near this ceiling.
const fetchEnrolledRows = async (tournamentId) => {
  const res = await tournamentParticipantAPI.getByTournamentId(tournamentId, 1, 1000);
  return (res?.data || []).filter(isPlayingRow);
};

// 'doubles' pairs are registered the same way as full 'teams' rosters in
// this data model — a `team` entity with `teamId` set, just with 2 members
// instead of 4+ — so the real/vs-individual split has to key off the row's
// own `teamId`, not off which participantType string is currently selected.
// Only the *roster fetch* below is gated on `participantType === 'teams'`,
// since a doubles pair only ever needs one display name ("Beaumont/Schmidt"),
// same as mockParticipantsService's doubles pool (no `roster` field at all).
const isTeamRow = (row) => !!row.teamId;

const rowsForType = (rows, participantType) => (participantType === 'singles' ? rows.filter((r) => !isTeamRow(r)) : rows.filter(isTeamRow));

const rowId = (row) => (isTeamRow(row) ? row.teamId : row.participantId || row.id);

const toParticipant = (row, index) =>
  isTeamRow(row)
    ? { id: row.teamId, name: row.teamName || row.team?.teamName || `Team ${row.teamId}`, seed: index + 1 }
    : {
        id: row.participantId || row.id,
        name: row.participantName || `${row.participant?.firstName || ''} ${row.participant?.lastName || ''}`.trim(),
        seed: index + 1,
      };

/**
 * A team's real roster, shaped like mockParticipantsService's buildRoster
 * output. The real data model has no gender field at all — always `null`
 * here, same as mock's filler bench players — since tieFormats.js already
 * treats gender as an optional, organizer-editable override rather than a
 * required field (see its defaultExcludedPairKeys/pairRole).
 */
const fetchRoster = async (teamId, playersPerTeam) => {
  const team = await teamAPI.getById(teamId);
  const members = (team?.members || []).filter((m) => m.isActive).slice(0, playersPerTeam);
  return members.map((m) => ({ id: m.user.id, name: `${m.user.firstName} ${m.user.lastName}`.trim(), gender: null }));
};

export const createRealParticipantsProvider = (tournamentId) => ({
  getParticipants: async (participantType, count, playersPerTeam) => {
    if (!tournamentId) return [];
    const rows = dedupeById(rowsForType(await fetchEnrolledRows(tournamentId), participantType), rowId);
    const sliced = rows.slice(0, count ?? rows.length).map((row, i) => toParticipant(row, i));
    if (participantType !== 'teams') return sliced;
    return Promise.all(sliced.map(async (team) => ({ ...team, roster: await fetchRoster(team.id, playersPerTeam) })));
  },

  getMaxParticipants: async (participantType) => {
    if (!tournamentId) return 0;
    const rows = rowsForType(await fetchEnrolledRows(tournamentId), participantType);
    return dedupeById(rows, rowId).length;
  },
});
